import React, { useEffect, useState } from "react";
import api, { fmtDate, fmtINR } from "@/lib/api";
import { PageHeader, DataTable, PrimaryButton } from "@/components/DataViews";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, CheckCircle2, AlertTriangle, Banknote } from "lucide-react";
import ExportButton from "@/components/ExportButton";

export default function InvoicesList() {
  const [items, setItems] = useState([]);
  const [pvs, setPvs] = useState([]);
  const [grns, setGrns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ po_id: "", grn_id: "", invoice_no: "", invoice_date: "", invoice_amount: 0, gst_amount: 0, advance_adjusted: 0, remarks: "" });
  const { hasRole } = useAuth();
  const canCreate = hasRole("Accounts Executive", "Accounts Manager", "Administrator");
  const canBookPV = hasRole("Accounts Executive", "Administrator");

  const load = () => Promise.all([
    api.get("/invoices").then((r) => setItems(r.data)),
    api.get("/pvs").then((r) => setPvs(r.data)).catch(() => setPvs([])),
  ]);
  useEffect(() => {
    load();
    api.get("/grns").then((r) => setGrns(r.data));
  }, []);

  const hasPV = (invId) => pvs.some((p) => p.invoice_id === invId && !p.is_voided);

  const openNew = () => {
    setForm({ po_id: "", grn_id: "", invoice_no: "", invoice_date: new Date().toISOString().slice(0, 10), invoice_amount: 0, gst_amount: 0, advance_adjusted: 0, remarks: "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      const { data } = await api.post("/invoices", form);
      toast.success(data.three_way_match ? "3-way match successful — Invoice Verified" : "Invoice created — Match mismatch, needs review");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const verify = async (id) => {
    try { await api.post(`/invoices/${id}/verify`); toast.success("Invoice verified"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bookPV = async (invId) => {
    try {
      const { data } = await api.post("/pvs", { invoice_id: invId, voucher_kind: "Invoice Payment", tds_pct: 0, retention_pct: 0, payment_mode: "NEFT" });
      toast.success(`Payment voucher ${data.pv_no} created`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Invoice Verification"
        subtitle="Three-way match: PO ↔ GRN ↔ Invoice. Matches within 5% tolerance auto-verify."
        actions={
          <>
            <ExportButton entity="invoices" filename="invoices.xlsx" />
            {canCreate && <PrimaryButton onClick={openNew} data-testid="new-invoice-btn"><Plus size={14} className="mr-2" />New Invoice</PrimaryButton>}
          </>
        }
      />
      <DataTable
        testid="invoices-table"
        columns={[
          { key: "invoice_no", label: "Invoice No", render: (r) => <span className="font-mono text-xs font-semibold">{r.invoice_no}</span> },
          { key: "vendor_name", label: "Vendor" },
          { key: "po_number", label: "PO", render: (r) => <span className="font-mono text-xs">{r.po_number}</span> },
          { key: "grn_no", label: "GRN", render: (r) => <span className="font-mono text-xs">{r.grn_no}</span> },
          { key: "invoice_amount", label: "Amount", render: (r) => <span className="font-mono">{fmtINR(r.invoice_amount)}</span> },
          { key: "advance_adjusted", label: "Adv Adj", render: (r) => r.advance_adjusted > 0 ? <span className="font-mono text-kabir-dark text-xs">-{fmtINR(r.advance_adjusted)}</span> : <span className="text-muted-foreground text-xs">—</span> },
          { key: "three_way_match", label: "3-Way Match", render: (r) => r.three_way_match ? <CheckCircle2 size={16} className="text-emerald-600 inline" /> : <AlertTriangle size={16} className="text-amber-600 inline" /> },
          { key: "status", label: "Status", render: (r) => (
            <div className="flex items-center gap-2">
              <StatusBadge status={r.status} />
              {r.status === "Pending" && canCreate && <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); verify(r.id); }} data-testid={`verify-${r.id}`}>Verify</Button>}
              {r.status === "Verified" && canBookPV && !hasPV(r.id) && (
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); bookPV(r.id); }} data-testid={`book-pv-${r.id}`}>
                  <Banknote size={12} className="mr-1" />Book PV
                </Button>
              )}
              {hasPV(r.id) && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold uppercase tracking-widest">PV Booked</span>}
            </div>
          )},
        ]}
        data={items}
        empty="No invoices yet."
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs uppercase font-semibold">GRN *</Label>
            <Select value={form.grn_id} onValueChange={(v) => {
              const g = grns.find((x) => x.id === v);
              setForm({ ...form, grn_id: v, po_id: g?.po_id || "" });
            }}>
              <SelectTrigger data-testid="inv-grn"><SelectValue placeholder="Select GRN" /></SelectTrigger>
              <SelectContent>{grns.map((g) => <SelectItem key={g.id} value={g.id}>{g.grn_no} — {g.po_number}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase font-semibold">Invoice No *</Label><Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="inv-no" /></div>
            <div><Label className="text-xs uppercase font-semibold">Invoice Date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Invoice Amount *</Label><Input type="number" value={form.invoice_amount} onChange={(e) => setForm({ ...form, invoice_amount: parseFloat(e.target.value) || 0 })} data-testid="inv-amount" /></div>
            <div><Label className="text-xs uppercase font-semibold">GST Amount</Label><Input type="number" value={form.gst_amount} onChange={(e) => setForm({ ...form, gst_amount: parseFloat(e.target.value) || 0 })} /></div>
            <div className="md:col-span-2"><Label className="text-xs uppercase font-semibold">Advance to Adjust (0 = auto)</Label><Input type="number" value={form.advance_adjusted} onChange={(e) => setForm({ ...form, advance_adjusted: parseFloat(e.target.value) || 0 })} data-testid="inv-advance" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <PrimaryButton onClick={save} data-testid="save-invoice">Save &amp; Match</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
